// apps/client/src/state/slices/world.ts — revision 10 item 3: the §8.7 WorldSlice exists from W2, so screens written before
// M0-T18 (M0-T17's `invites`, W5) read typed world fields. M0-T18 (W5) fills applySnapshot/applyPatch; nothing else changes here.
import { fromPayload } from '@whiskerton/shared';
import type { CatalogId, DayKey, MapData, PresenceEntry, PrivatePatch, PrivateState, ResidentId, ResidentPublic, Snapshot, SpaceId, TownId, TownInfo } from '@whiskerton/shared';
import type { StringKey } from '@whiskerton/content';
import type { SliceCreator } from '../types';
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging into GameStore
declare module '../types' { interface GameStore extends WorldSlice {} }
/** Re-exported by net/socket.ts (M0-T18). */
export type SocketState = 'idle' | 'ticket' | 'connecting' | 'live' | 'backoff' | 'updateRequired' | 'closed';
export interface WorldSlice {
  townId: TownId | null; town: TownInfo | null;
  residents: Record<ResidentId, ResidentPublic>; presence: Record<ResidentId, PresenceEntry>;
  you: PrivateState | null; space: SpaceId;
  clock: { minuteOfDay: number; weekday: number; dayKey: DayKey | null };
  prompt: { verb: StringKey; candidateId: string } | null;
  socket: SocketState;
  tool: CatalogId | null; setTool(id: CatalogId | null): void;   // rev 4: equipped tool → MoveState.tool and InteractCtx.tool (M1-T13 cycles it)
  map: MapData | null;                                           // rev 4: fromPayload(snapshot.map)
  applySnapshot(s: Snapshot): void; applyPatch(p: PrivatePatch): void;
}
/** Upserts `add` by key (replacing same-key entries in place order: kept entries first, then the new ones) and drops `remove`. */
const upsert = <T, K>(list: readonly T[], add: readonly T[], key: (x: T) => K, remove: readonly K[] = []): T[] => {
  const drop = new Set<K>([...remove, ...add.map(key)]);
  return [...list.filter((x) => !drop.has(key(x))), ...add];
};
export const slice: SliceCreator<WorldSlice> = (set, get) => ({
  townId: null, town: null, residents: {}, presence: {}, you: null, space: 'out',
  clock: { minuteOfDay: 0, weekday: 0, dayKey: null }, prompt: null, socket: 'idle', tool: null, map: null,
  setTool: (id) => set({ tool: id }),
  applySnapshot: (s) => set({
    town: s.town, townId: s.town.id, you: s.you, space: s.space.space, map: s.map ? fromPayload(s.map) : null,
    residents: Object.fromEntries(s.residents.map((r) => [r.id, r])) as Record<ResidentId, ResidentPublic>,
    presence: Object.fromEntries(s.presence.map((p) => [p.r, p])) as Record<ResidentId, PresenceEntry>,
  }),
  applyPatch: (p) => {
    const you = get().you;
    if (!you) return;
    const next: PrivateState = { ...you };
    if (p.bells !== undefined) next.bells = p.bells;
    if (p.loan !== undefined) next.loan = p.loan;
    if (p.items) next.items = upsert(you.items, p.items.upsert, (i) => i.id, p.items.remove);
    if (p.mail) next.mail = upsert(you.mail, p.mail.upsert, (m) => m.id, p.mail.remove).sort((a, b) => b.at - a.at);
    if (p.yields) next.yields = { ...you.yields, ...p.yields };
    if (p.harvests) next.harvests = { ...you.harvests, ...p.harvests };
    if (p.fishLog) next.fishLog = upsert(you.fishLog, p.fishLog, (f) => f.catalogId);
    if (p.shop) next.shop = p.shop;
    if (p.friendships) next.friendships = upsert(you.friendships, p.friendships, (f) => f.villager);
    if (p.settings) next.settings = p.settings;
    const patch: Partial<WorldSlice> = { you: next };
    if (p.house) {
      const me = get().residents[you.resident];
      if (me) patch.residents = { ...get().residents, [me.id]: { ...me, houseLevel: p.house.level, pendingUpgrade: p.house.pending, roof: p.house.roof, door: p.house.door } };
    }
    set(patch);
  },
});
